import Pusher, {AuthOptions, Channel as PusherChannel} from 'pusher-js'

import 'powtoon-commons/types'
import {EventBus} from 'powtoon-commons/events'
import {PusherEventConverter} from './types'

import {uniq} from 'lodash'

export interface PusherConfig {
  pusherKey: string,
  userId: string,
  sessionId: string,
  csrfToken: string,
  apiHost: string,
  cluster?: 'mt1' | 'us2' | 'us3' | 'eu' | 'ap1' | 'ap2' | 'ap3' | 'ap4'
}

export type PusherChannelEvent = 'message' | 'client-event' | 'server_event' | 'pusher:member_removed' |
  'pusher:member_added'

export class PusherListener {
  private converters: PusherEventConverter[]
  private readonly eventBus: EventBus
  private readonly logger: any
  public readonly pusher: Pusher
  private readonly config: PusherConfig
  private readonly channelsMap: Map<string, PusherChannel>

  constructor(
    config: PusherConfig,
    eventConverters: PusherEventConverter[],
    eventBus: EventBus,
    logger: any = console
  ) {

    this.handleEvent = this.handleEvent.bind(this)
    this.converters = eventConverters || []
    this.eventBus = eventBus
    this.logger = logger
    this.config = config
    this.channelsMap = new Map<string, PusherChannel>()

    this.pusher = this._connect()
    this._initialChannelsSubscribe()
  }

  private _connect(): Pusher {
    const config = this.config

    const getCluster = () => config.cluster ? {cluster: config.cluster} : {}

    if (!(window as any).powtoon_pusher) {

      this.logger.log('new Pusher will be created!')
      const pusher = new Pusher(config.pusherKey, {
        authEndpoint: `${config.apiHost}/notification/pusher/auth`,
        auth: {
          headers: {
            'X-CSRFToken': config.csrfToken,
            'X-SESSION': config.sessionId
          }
        } as AuthOptions,
        ...getCluster()
      })
      this.logger.log('new Pusher Connected!')
      return pusher
    }
    else {
      this.logger.log('existing Pusher Connected!')
      return window.powtoon_pusher
    }
  }

  private _initialChannelsSubscribe() {
    this.subscribe(`private-user-${this.config.userId}`)
    this.subscribe('update_channel')
  }

  private handleEvent(eventName: string, eventData: any) {
    let convertedEvent
    
    this.logger.debug(`@@@ Event received: ${eventName}, ${JSON.stringify(eventData)}`)
    this.logger.log(`Event received: ${eventName}, ${JSON.stringify(eventData)}`)

    for (const converter of this.converters) {
      try {
        convertedEvent = converter(eventName, eventData)
        if (convertedEvent) {
          break
        }
      } catch (e) {
        this.logger.error(new Error('eventConverter exception.'), {exception: e, eventName, eventData})
      }
    }

    if (convertedEvent) {
      this.eventBus.publish(convertedEvent)
      this.logger.log(`Event processed and published to eventBus: ${eventName}, ${JSON.stringify(convertedEvent)}`)
    }
  }

  public subscribe(
    channelName: string,
    events2bindList?: PusherChannelEvent[]
  ): PusherChannel {

    if (this.channelsMap.has(channelName)) {
      return this.channelsMap.get(channelName) as PusherChannel
    }

    events2bindList = ['message', 'client-event', 'server_event', ...(events2bindList || [])]

    const channel = this.pusher.subscribe(channelName)

    events2bindList.forEach(eventName => {
      channel.bind(eventName, (data: any) => this.handleEvent(eventName, data))
    })

    this.channelsMap.set(channelName, channel)

    this.logger.log(`Subscribed to channel ${channelName}`)

    return channel
  }


  public unsubscribe(channelName: string) {
    if (this.channelsMap.has(channelName)) {
      const channel = this.channelsMap.get(channelName) as PusherChannel
      channel.unsubscribe()
      this.channelsMap.delete(channelName)

      this.logger.log(`Unsubscribed from channel ${channelName}`)
    }
  }

  public unsubscribeAllChannels() {
    this.channelsMap.forEach((channel, channelName) => {
      this.unsubscribe(channelName)
    })
  }

  public registerEventConverters(eventConverters: PusherEventConverter[]) {
    this.converters = uniq([
      ...this.converters,
      ...eventConverters
    ])
  }
}
